import React from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { useArticles } from '../contexts/ArticleContext';
import { categories } from '../data/categories';
import ArticleCard from '../components/molecules/ArticleCard';
import { SectionTitle } from '../styles/commonStyles';
import { SubCategoryType, Article } from '../types';
import SEO from '../components/SEO';

const SubCategoryContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${theme.space.xl} ${theme.space.md};
`;

const SubCategoryHeader = styled.div`
  margin-bottom: ${theme.space.xl};
  text-align: center;
  margin-top: ${theme.space.xl};
`;

const SubCategoryTitleWrapper = styled.div`
  margin-top: ${theme.space.xl};
  margin-bottom: ${theme.space.xl};
`;

const SubCategoryDescription = styled.p`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.white};
  margin-top: ${theme.space.md};
`;

const CategoryLink = styled(Link)`
  color: ${theme.colors.white};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbNav = styled.nav`
  margin-bottom: ${theme.space.lg};
  
  a {
    color: ${theme.colors.white};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  span {
    margin: 0 ${theme.space.xs};
    color: ${theme.colors.white};
  }
`;

const ArticlesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space.md};
`;

const NoArticlesMessage = styled.p`
  text-align: center;
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.white};
  padding: ${theme.space.xl} 0;
`;

const PageWrapper = styled.div`
  background-color: ${theme.colors.primary};
  color: ${theme.colors.white};
  min-height: 100vh;
  padding-bottom: ${theme.space.xl};
  position: relative;
  overflow-x: hidden;
  width: 100%;
`;

const HomeButton = styled(Link)`
  background-color: #D67616;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 30px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s;
  display: block;
  margin: 30px auto 0;
  text-align: center;
  width: fit-content;
  text-decoration: none;
  
  &:hover {
    background-color: #e55a00;
  }
`;

const LoadingMessage = styled.p`
  text-align: center;
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.white};
  padding: ${theme.space.xl} 0;
`;

const ErrorMessage = styled.p`
  text-align: center;
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.white};
  padding: ${theme.space.xl} 0;
`;

const SubCategoryPage: React.FC = () => {
  const { articles, loading, error } = useArticles();
  const { categoryName, subCategoryName } = useParams<{ categoryName: string; subCategoryName: string }>();

  // カテゴリとサブカテゴリの情報を取得
  const category = categories.find(cat => cat.name === categoryName);
  
  // サブカテゴリに属する記事を取得
  const subCategoryArticles = articles.filter(
    (article: Article) => article.category === categoryName && 
      article.subCategory && article.subCategory.includes(subCategoryName)
  );

  if (loading) {
    return (
      <PageWrapper>
        <SubCategoryContainer>
          <LoadingMessage>読み込み中...</LoadingMessage>
        </SubCategoryContainer>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <SubCategoryContainer>
          <ErrorMessage>エラーが発生しました: {error}</ErrorMessage>
        </SubCategoryContainer>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <SEO 
        title={`${subCategoryName} - ${categoryName}カテゴリー`}
        description={`アラヨットラベル - ${categoryName}カテゴリの${subCategoryName}に関する旅行記事一覧です。`}
        keywords={`${categoryName}, ${subCategoryName}, 旅行, 観光, アラヨットラベル`}
        canonical={`/category/${categoryName}/${subCategoryName}`}
      />
      
      <SubCategoryContainer>
        <BreadcrumbNav>
          <Link to="/">ホーム</Link>
          <span>/</span>
          <CategoryLink to={`/category/${categoryName}`}>{categoryName}</CategoryLink>
          <span>/</span>
          <span>{subCategoryName}</span>
        </BreadcrumbNav>

        <SubCategoryHeader>
          <SubCategoryTitleWrapper>
            <SectionTitle>{subCategoryName}</SectionTitle>
          </SubCategoryTitleWrapper>
          <SubCategoryDescription>{categoryName}カテゴリの{subCategoryName}に関する記事</SubCategoryDescription>
        </SubCategoryHeader>

        {subCategoryArticles.length > 0 ? (
          <ArticlesList>
            {subCategoryArticles.map((article: Article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </ArticlesList>
        ) : (
          <NoArticlesMessage>この「{subCategoryName}」の記事はまだありません。</NoArticlesMessage>
        )}
        
        <HomeButton to="/" onClick={() => window.scrollTo(0, 0)}>ホームへ戻る</HomeButton>
      </SubCategoryContainer>
    </PageWrapper>
  );
};

export default SubCategoryPage;